import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatHours, formatRange } from '@jupi/ui'
import { neighbouringBookings } from '@jupi/shared'
import type { Booking } from '../api/queries'
import { BOOKING_TYPE_ICONS, typeOf } from '../features/calendar/bookingType'

// The booking before and the booking after the chosen slot on the same
// aircraft, with the free time between each and the slot, so a member can
// see whether there is room to run over or who is waiting behind them.
export function BookingNeighbours({
  bookings,
  registration,
  window,
  excludeBookingId,
}: {
  bookings: readonly Booking[]
  registration: string
  window: { startsAt: Date; endsAt: Date } | null
  excludeBookingId?: number
}) {
  const { t } = useTranslation()
  if (!registration || !window) return null
  const { previous, next } = neighbouringBookings(bookings, registration, window, excludeBookingId)

  const row = (label: string, booking: Booking | null, gapMs: number | null) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 56, flexShrink: 0 }}>
        {label}
      </Typography>
      {booking ? (
        <>
          <Icon icon={BOOKING_TYPE_ICONS[typeOf(booking)]} width={16} />
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {formatRange(booking.startsAt, booking.endsAt)} · {booking.displayName}
          </Typography>
          {gapMs !== null ? (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {t('booking.neighbours.gap', { hours: formatHours(gapMs / 3_600_000) })}
            </Typography>
          ) : null}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('booking.neighbours.none')}
        </Typography>
      )}
    </Stack>
  )

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {t('booking.neighbours.title', { aircraft: registration })}
      </Typography>
      <Stack spacing={0.5}>
        {row(t('booking.neighbours.previous'), previous, previous ? window.startsAt.getTime() - new Date(previous.endsAt).getTime() : null)}
        {row(t('booking.neighbours.next'), next, next ? new Date(next.startsAt).getTime() - window.endsAt.getTime() : null)}
      </Stack>
    </Box>
  )
}
